// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { numberToFixed } from '@shared/ui-kit/utils';
import { useNavigate } from 'react-router';
import type { Paths } from '@shared/data-access/interfaces/routes/paths';
import { useDispatch } from 'react-redux';
import { portfolioPositionsSlice } from '@shared/data-access/slices/portfolio-positions';
import { Button, Slider } from '@mui/material';
import { type PortfolioPositionsCurrent } from '@shared/data-access/interfaces/redux/portfolio-state';
import { Heading, useDynamicDialog } from '@shared/ui-kit/components';
import { type LoadPosition } from '@shared/ui-kit/components/popups/asset-profile/hooks';
import { useCallback, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { useTranslation } from 'react-i18next';

export interface InvestPopupProps {
  onSubmit: (investment: number) => void;
}

const PORTFOLIO_INVESTMENT_LIMIT = 10_000_000;
const PORTFOLIO_INVESTMENT_MIN = 50_000;

export const useInvestmentPopup = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { openDialog, closeDialog } = useDynamicDialog();

  const createPortfolioPositions = useCallback(
    (
      positions: LoadPosition[],
      investment: number,
    ): PortfolioPositionsCurrent[] => {
      const totalWeight = positions.reduce((acc, { weight }) => acc + weight, 0);
      return positions.map(({ ticker, weight }) => {
        const scaledWeight = weight / totalWeight;
        return {
          ticker,
          qty: numberToFixed(investment * scaledWeight, 2),
          price: 1,
          unrealized_capital_gains: 0,
        };
      });
    },
    [],
  );

  const loadPortfolioPositions = useCallback(
    (positions: LoadPosition[], investment: number, redirectPath: Paths) => {
      const portfolio = createPortfolioPositions(positions, investment);
      dispatch(
        portfolioPositionsSlice.actions.setCurrentPortfolioPositions(portfolio),
      );
      closeDialog();
      navigate(redirectPath);
    },
    [closeDialog, createPortfolioPositions, dispatch, navigate],
  );

  const openInvestmentPopup = useCallback(
    (positions: LoadPosition[], redirectPath: Paths) => {
      openDialog(
        <InvestmentPopup
          onSubmit={investment =>
            loadPortfolioPositions(positions, investment, redirectPath)
          }
        />,
        { disableBackdropClick: true },
      );

      return positions;
    },
    [openDialog, loadPortfolioPositions],
  );

  return { openInvestmentPopup };
};

const InvestmentPopup = ({ onSubmit }: InvestPopupProps) => {
  const { t } = useTranslation();
  const [investment, setInvestment] = useState(PORTFOLIO_INVESTMENT_MIN);

  const handleInvestmentChange = useCallback((value: number) => {
    // Ensure investment is within the limits
    const investment = Math.min(
      PORTFOLIO_INVESTMENT_LIMIT,
      Math.max(PORTFOLIO_INVESTMENT_MIN, value),
    );

    setInvestment(investment);
  }, []);

  return (
    <div className="grid gap-4">
      <Heading
        variant="h2"
        title={t('dashboard.how-much-money-invest', { ns: 'dashboard' })}
      />
      <div className="grid gap-4 rounded-lg bg-section p-8">
        <Heading
          variant="h3"
          title={t('dashboard.investment', { ns: 'dashboard' })}
        />
        <NumericFormat
          id="investment-input"
          data-testid="investment-input"
          value={investment}
          thousandSeparator={true}
          allowNegative={false}
          onValueChange={({ floatValue }) => handleInvestmentChange(floatValue!)}
          prefix="$"
          className="input-common"
        />
        <Slider
          aria-label="Investment amount"
          data-testid="investment-slider"
          valueLabelDisplay="off"
          onChange={(_, value) => handleInvestmentChange(value as number)}
          value={investment}
          min={PORTFOLIO_INVESTMENT_MIN}
          max={PORTFOLIO_INVESTMENT_LIMIT}
          step={1_000}
        />
      </div>
      <div className="flex justify-end">
        <Button
          variant="contained"
          color="secondary"
          onClick={() => onSubmit(investment)}
        >
          {t('dashboard.invest', { ns: 'dashboard' })}
        </Button>
      </div>
    </div>
  );
};
